import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { axiosInstance } from "../../../../api/axiosInstance";
import { DuesRules, downloadStudentTemplate, duesHeader, loadDuesRules } from "../service/studentImportService";
import { mapRowToStudentDTO } from "../service/studentImportMapper";
import { ParseResult, parseStudentImportFile } from "../service/studentImportValidator";

// Mirrors the backend's StudentImportResponseDto (only FAILED students are returned)
export interface FailedImportRow {
  name?: string;
  admissionNo?: string;
  status?: string;
  message?: string;
}

export interface ImportOutcome {
  sent: number;
  imported: number;
  failed: FailedImportRow[];
}

export interface UploadedFile {
  name: string;
  size: number;
}

// Backend errors come as a plain string, { message }, a list, or a validation failure
// { error, details: [{ field, message }] } (GlobalExceptionHandler) - turned into readable text
const errorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  const fieldError = (e: any) => (e?.field ? `${e.field}: ${e.message}` : e?.message ?? String(e));
  if (typeof data === "string" && data.trim()) return data;
  if (Array.isArray(data)) return data.map(fieldError).join("; ");
  if (Array.isArray(data?.details) && data.details.length > 0)
    return `${data.error ?? "Validation failed"}: ${data.details.map(fieldError).join("; ")}`;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  return err?.response ? fallback : "Could not reach the server. Check your connection and try again.";
};

const saveWorkbook = (rows: Record<string, any>[], sheetName: string, fileName: string) => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), sheetName);
  XLSX.writeFile(workbook, fileName);
};

export const useStudentImport = () => {
  const [rules, setRules] = useState<DuesRules | null>(null);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const [file, setFile] = useState<UploadedFile | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);

  const [importing, setImporting] = useState(false);
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Dues years, grade order and fee limits (all from the app cache) drive the template and the checks
  useEffect(() => {
    loadDuesRules()
      .then(setRules)
      .catch(() => setRulesError("Failed to load academic years and fee structures."));
  }, []);

  // Template for one grade + section, so each dues column can carry a fixed maximum
  const downloadTemplate = async (grade: string, section: string) => {
    setDownloading(true);
    try {
      await downloadStudentTemplate(grade, section);
    } finally {
      setDownloading(false);
    }
  };

  const resetFile = () => {
    setFile(null);
    setParseResult(null);
    setOutcome(null);
    setImportError(null);
  };

  const readFile = async (selected: File) => {
    resetFile();
    setFile({ name: selected.name, size: selected.size });
    setParsing(true);
    try {
      // Reload in case fees were set since the page opened (served from the cache when unchanged)
      const currentRules = await loadDuesRules();
      setRules(currentRules);
      const workbook = XLSX.read(await selected.arrayBuffer(), { type: "array" });
      setParseResult(parseStudentImportFile(workbook, currentRules));
    } catch (err) {
      setParseResult({
        importInfo: null,
        rows: [],
        fileErrors: ["This file could not be read. Upload the .xlsx template filled in Excel."],
        rowIssues: [],
      });
    } finally {
      setParsing(false);
    }
  };

  const isValid = !!parseResult && parseResult.fileErrors.length === 0 && parseResult.rowIssues.length === 0 && parseResult.rows.length > 0;

  const submit = async () => {
    if (!parseResult?.importInfo || !isValid) return;
    const importInfo = parseResult.importInfo;
    const studentDTOS = parseResult.rows.map((row) => mapRowToStudentDTO(row.data, importInfo, row.dues));

    setImporting(true);
    setImportError(null);
    try {
      const response = await axiosInstance.post("/students/import", { studentDTOS });
      if (!Array.isArray(response.data)) {
        setImportError("The server sent an unexpected response. Check the student list before importing again.");
        return;
      }
      const failed: FailedImportRow[] = response.data;
      setOutcome({ sent: studentDTOS.length, imported: Math.max(studentDTOS.length - failed.length, 0), failed });
    } catch (err) {
      // Never derive a success count from an error response
      setImportError(errorMessage(err, "The import failed. No students were confirmed as imported."));
    } finally {
      setImporting(false);
    }
  };

  // Validation problems as a sheet: one line per problem
  const downloadErrorReport = () => {
    if (!parseResult) return;
    const lines = [
      ...parseResult.fileErrors.map((problem) => ({ Row: "", "Admission No": "", Name: "", Problem: problem })),
      ...parseResult.rowIssues.flatMap((issue) =>
        issue.problems.map((problem) => ({ Row: issue.rowNumber, "Admission No": issue.admissionNo, Name: issue.name, Problem: problem }))
      ),
    ];
    saveWorkbook(lines, "Problems", "Student_Import_Problems.xlsx");
  };

  // Failed students with their original columns plus the reason, ready to fix and re-upload
  const downloadFailedRows = () => {
    if (!outcome || !parseResult) return;
    const reasons = new Map(outcome.failed.map((f) => [(f.admissionNo ?? "").toLowerCase(), f.message ?? f.status ?? "FAILED"]));
    const rows = parseResult.rows
      .filter((row) => reasons.has(row.admissionNo.toLowerCase()))
      .map((row) => ({ "Import Error": reasons.get(row.admissionNo.toLowerCase()), ...row.data }));
    saveWorkbook(rows, "Students", "Student_Import_Failed.xlsx");
  };

  return {
    rules,
    rulesError,
    duesHeaders: rules?.duesYears.map(duesHeader) ?? [],
    downloading,
    downloadTemplate,
    file,
    parsing,
    parseResult,
    isValid,
    readFile,
    resetFile,
    importing,
    outcome,
    importError,
    submit,
    downloadErrorReport,
    downloadFailedRows,
  };
};
